import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BoltIcon from '@mui/icons-material/Bolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ewSmallLogo from '../../../assets/images/ew-small-logo.webp';

const TRIAL_START_DATE = 'March 6, 2026';

const plans = [
  {
    id: 'Live-Webinars-USD-Monthly-50',
    name: 'Launch',
    description: 'Best for small live webinars',
    priceToday: '$1',
    priceMonthly: 44,
    icon: RocketLaunchIcon,
    iconSx: { color: '#6366f1' },
    features: [
      '50 Live Attendees',
      'Up to 5 webinars',
      'AI Builder (2 funnels)',
      'Live chat & polls',
      'Standard support',
    ],
    popular: false,
    buttonVariant: 'outlined' as const,
  },
  {
    id: 'All-In-One-USD-Monthly-200',
    name: 'Pro',
    description: 'Best for live, simulive & evergreen',
    priceToday: '$1',
    priceMonthly: 116,
    icon: BoltIcon,
    iconSx: { background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', color: '#8B5CF6' },
    features: [
      '200 Live + 1,000 Auto Attendees',
      'Unlimited webinars',
      'AI Builder (5 funnels)',
      'EasyCRM + Paid Webinars',
      'Priority support + Manager',
    ],
    popular: true,
    buttonVariant: 'contained' as const,
  },
  {
    id: 'All-In-One-USD-Monthly-500',
    name: 'Growth',
    description: 'Best for high-volume webinars',
    priceToday: '$1',
    priceMonthly: 144,
    icon: TrendingUpIcon,
    iconSx: { background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', color: '#3B82F6' },
    features: [
      '500 Live + 2,500 Auto Attendees',
      'Unlimited webinars',
      'AI Builder (10 funnels)',
      'EasyCRM + Paid Webinars',
      'Priority support + Manager',
    ],
    popular: false,
    buttonVariant: 'outlined' as const,
  },
] as const;

export default function PricingPage() {
  const navigate = useNavigate();
  const handleSelectPlan = useCallback(
    (planId: string) => {
      navigate(`/try-ai-flow/payment?plan=${planId}`);
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        py: 4,
        background:
          'linear-gradient(180deg, rgb(255, 245, 250) 0%, rgb(250, 245, 255) 35%, rgb(243, 238, 252) 70%, rgb(237, 233, 248) 100%)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Link
          to="/try-ai-flow"
          style={{
            flexShrink: 0,
            marginBottom: 24,
            display: 'block',
            borderRadius: 8,
          }}
        >
          <img
            src={ewSmallLogo}
            alt="EasyWebinar"
            style={{ display: 'block', height: 40 }}
          />
        </Link>

        {/* 7-Day Trial banner */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: 'rgba(34, 197, 94, 0.12)',
            border: '1px solid rgba(34, 197, 94, 0.4)',
            mb: 2,
          }}
        >
          <CheckIcon sx={{ fontSize: 18, color: '#22c55e' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#15803d' }}>
            7-Day Trial - Cancel Anytime
          </Typography>
        </Box>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            textAlign: 'center',
            mb: 0.5,
            lineHeight: 1.25,
          }}
        >
          Start Your 7-Day Trial to Publish
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 4,
            maxWidth: 480,
          }}
        >
          Select a plan to publish your webinar and unlock full access.
        </Typography>

        {/* Pricing cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <Paper
                key={plan.id}
                elevation={0}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  border: plan.popular
                    ? '2px solid transparent'
                    : '1px solid rgba(0,0,0,0.08)',
                  background: plan.popular
                    ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%) border-box'
                    : '#fff',
                  boxShadow: plan.popular
                    ? '0 8px 32px rgba(139, 92, 246, 0.15)'
                    : '0 4px 20px rgba(0,0,0,0.06)',
                  p: 3,
                  flex: 1,
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: 16,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: '#8B5CF6',
                      boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                      MOST POPULAR
                    </Typography>
                  </Box>
                )}

                {/* Centered icon at top */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 1.5,
                  }}
                >
                  <Icon sx={{ fontSize: 40, ...plan.iconSx }} />
                </Box>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    textAlign: 'center',
                    mb: 0.25,
                  }}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                    mb: 2,
                  }}
                >
                  {plan.description}
                </Typography>

                {/* Pricing in light purple box */}
                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'rgba(139, 92, 246, 0.08)',
                    border: '1px solid rgba(139, 92, 246, 0.18)',
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#5b5fc7' }}>
                    {plan.priceToday}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 400, color: 'text.primary', ml: 0.5 }}>
                    today
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                    Then{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>${plan.priceMonthly}/month</Box>
                    {' '}starting on{' '}
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{TRIAL_START_DATE}</Box>
                  </Typography>
                </Box>

                {/* Features with light blue checkmarks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, mb: 2 }}>
                  {plan.features.map((feature) => (
                    <Box
                      key={feature}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 1,
                          bgcolor: 'rgba(59, 130, 246, 0.15)',
                          border: '1px solid rgba(59, 130, 246, 0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Centered CTA: white bg, thin purplish-blue border */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleSelectPlan(plan.id)}
                  sx={{
                    mt: 'auto',
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.9375rem',
                    bgcolor: '#fff',
                    borderColor: 'rgba(91, 95, 199, 0.5)',
                    ...(plan.popular
                      ? {
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                          color: '#fff',
                          border: 'none',
                          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.25)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)',
                            border: 'none',
                            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                          },
                        }
                      : {
                          '&:hover': {
                            borderColor: '#5b5fc7',
                            bgcolor: 'rgba(91, 95, 199, 0.05)',
                          },
                        }),
                  }}
                >
                  Select {plan.name}
                </Button>
              </Paper>
            );
          })}
        </Box>

        {/* Footer */}
        <Typography
          component={Link}
          to="/try-ai-flow"
          variant="body2"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 4,
            color: 'text.secondary',
            textDecoration: 'none',
            '&:hover': { color: '#8B7CB8', textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          Back to draft
        </Typography>
      </Box>
    </Box>
  );
}
